/**
 * Site-wide audio player for audio posts (Audiobeiträge).
 *
 * One detached Audio element lives in JS memory for the whole visit, and the sticky player bar
 * (`@persist('audio-player')` in the layout) and every inline play button read from the
 * `audio` store. While a track is loaded, same-origin links and GET forms go through
 * Livewire's SPA navigation so the page never unloads and playback continues. Without a
 * track the site keeps its normal full page loads.
 */

const PLAYBACK_RATES = [1, 1.25, 1.5, 2];
const SESSION_KEY = 'audio-player';
const SKIP_BACK_SECONDS = 15;
const SKIP_FORWARD_SECONDS = 30;

/** Paths that are files or feeds rather than pages, so SPA navigation must leave them alone. */
const NATIVE_PATH_PREFIXES = ['/audio-files/', '/download/', '/images/', '/thumbnails/', '/storage/', '/build/'];

/**
 * @typedef {{id: number, title: string, author: ?string, url: string, src: string, type: string, artwork: ?string}} AudioTrack
 */

/**
 * @param {import('alpinejs').Alpine} Alpine
 * @param {{navigate: (url: string) => void}} Livewire
 */
export function registerAudioPlayer(Alpine, Livewire) {
  const audio = new Audio();
  audio.preload = 'metadata';

  Alpine.store('audio', {
    /** @type {?AudioTrack} */
    track: null,
    playing: false,
    buffering: false,
    failed: false,
    currentTime: 0,
    duration: 0,
    rate: 1,

    format(seconds) {
      return formatTime(seconds);
    },

    isCurrent(id) {
      return this.track?.id === id;
    },

    isPlaying(id) {
      return this.isCurrent(id) && this.playing;
    },

    /** Progress of the given track from 0 to 1; 0 for any track that isn't loaded. */
    progress(id) {
      if (!this.isCurrent(id) || !this.duration) {
        return 0;
      }
      return Math.min(1, this.currentTime / this.duration);
    },

    /** @param {AudioTrack} track */
    toggle(track) {
      if (!this.isCurrent(track.id)) {
        this.load(track, { autoplay: true });
        return;
      }
      this.playing ? audio.pause() : this.resume();
    },

    togglePlayback() {
      this.playing ? audio.pause() : this.resume();
    },

    resume() {
      this.failed = false;
      audio.play().catch(() => {
        this.playing = false;
      });
    },

    /**
     * @param {AudioTrack} track
     * @param {{autoplay?: boolean, startAt?: number}} options
     */
    load(track, { autoplay = false, startAt = 0 } = {}) {
      this.track = track;
      this.failed = false;
      this.currentTime = startAt;
      this.duration = 0;
      audio.src = track.src;
      audio.playbackRate = this.rate;
      if (startAt > 0) {
        audio.addEventListener('loadedmetadata', () => (audio.currentTime = startAt), { once: true });
      }
      updateMediaMetadata(track);
      autoplay ? this.resume() : audio.load();
    },

    seek(seconds) {
      const target = Math.max(0, Math.min(Number(seconds), this.duration || Number(seconds)));
      audio.currentTime = target;
      this.currentTime = target;
    },

    skip(seconds) {
      this.seek(audio.currentTime + seconds);
    },

    cycleRate() {
      const next = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(this.rate) + 1) % PLAYBACK_RATES.length];
      this.rate = next;
      audio.playbackRate = next;
    },

    close() {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      this.track = null;
      this.playing = false;
      this.currentTime = 0;
      this.duration = 0;
      clearMediaSession();
      forgetSession();
    },
  });

  const store = Alpine.store('audio');

  audio.addEventListener('play', () => (store.playing = true));
  audio.addEventListener('pause', () => (store.playing = false));
  audio.addEventListener('ended', () => {
    store.playing = false;
    store.currentTime = 0;
    audio.currentTime = 0;
  });
  audio.addEventListener('waiting', () => (store.buffering = true));
  audio.addEventListener('playing', () => (store.buffering = false));
  audio.addEventListener('canplay', () => (store.buffering = false));
  audio.addEventListener('loadedmetadata', () => (store.duration = Number.isFinite(audio.duration) ? audio.duration : 0));
  audio.addEventListener('durationchange', () => (store.duration = Number.isFinite(audio.duration) ? audio.duration : 0));
  audio.addEventListener('timeupdate', () => {
    store.currentTime = audio.currentTime;
    updatePositionState(audio);
  });
  audio.addEventListener('error', () => {
    if (store.track) {
      store.failed = true;
      store.playing = false;
      store.buffering = false;
    }
  });

  registerMediaSessionHandlers(store, audio);
  restoreSession(store);
  window.addEventListener('pagehide', () => rememberSession(store, audio));

  Alpine.data('audioTrack', (track) => ({
    track,
    get current() {
      return this.$store.audio.isCurrent(this.track.id);
    },
    get playing() {
      return this.$store.audio.isPlaying(this.track.id);
    },
    get progress() {
      return this.$store.audio.progress(this.track.id);
    },
    get label() {
      return (this.playing ? 'Pause: ' : 'Anhören: ') + this.track.title;
    },
    toggle() {
      this.$store.audio.toggle(this.track);
    },
  }));

  interceptNavigationWhileAudioIsLoaded(store, Livewire);
}

/** Format seconds as m:ss or h:mm:ss. */
export function formatTime(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}` : `${minutes}:${secs}`;
}

function interceptNavigationWhileAudioIsLoaded(store, Livewire) {
  let navigatedByPlayer = false;

  document.addEventListener('click', (event) => {
    if (!store.track || event.defaultPrevented || event.button !== 0) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !shouldNavigateInPlace(link)) {
      return;
    }

    event.preventDefault();
    navigatedByPlayer = true;
    Livewire.navigate(link.href);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!store.track || event.defaultPrevented || !(form instanceof HTMLFormElement)) {
      return;
    }
    if (form.method.toLowerCase() !== 'get' || form.hasAttribute('wire:submit') || form.target) {
      return;
    }

    const url = new URL(form.action, window.location.href);
    if (url.origin !== window.location.origin) {
      return;
    }
    url.search = new URLSearchParams(new FormData(form)).toString();

    event.preventDefault();
    navigatedByPlayer = true;
    Livewire.navigate(url.toString());
  });

  // Move focus to the new content after an SPA navigation, as a full page load would, and let
  // screen readers know the page changed.
  document.addEventListener('livewire:navigated', () => {
    if (!navigatedByPlayer) {
      return;
    }
    navigatedByPlayer = false;
    document.getElementById('main')?.focus({ preventScroll: true });
    const announcer = document.getElementById('audio-player-page-announcer');
    if (announcer) {
      announcer.textContent = document.title;
    }
  });
}

/** @param {HTMLAnchorElement} link */
function shouldNavigateInPlace(link) {
  if (link.hasAttribute('download') || link.hasAttribute('data-no-navigate') || link.hasAttribute('wire:navigate')) {
    return false;
  }
  if (link.target && link.target !== '_self') {
    return false;
  }

  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) {
    return false;
  }
  // Same-page anchors scroll natively.
  if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) {
    return false;
  }
  if (NATIVE_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))) {
    return false;
  }
  // Files and feeds (anything with an extension, e.g. .pdf, .rss, .xml) are not pages.
  return !/\.[a-z0-9]+$/i.test(url.pathname);
}

function updateMediaMetadata(track) {
  if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') {
    return;
  }
  navigator.mediaSession.metadata = new MediaMetadata({
    title: track.title,
    artist: track.author ?? '',
    album: 'Audiobeiträge – Katholisch im Rhein-Kreis Neuss',
    artwork: track.artwork ? [{ src: track.artwork, sizes: '200x200' }] : [],
  });
}

function clearMediaSession() {
  if ('mediaSession' in navigator) {
    navigator.mediaSession.metadata = null;
  }
}

function updatePositionState(audio) {
  if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) {
    return;
  }
  if (!Number.isFinite(audio.duration) || audio.duration <= 0) {
    return;
  }
  try {
    navigator.mediaSession.setPositionState({
      duration: audio.duration,
      playbackRate: audio.playbackRate,
      position: Math.min(audio.currentTime, audio.duration),
    });
  } catch {
    // Some browsers reject position updates while metadata is still loading.
  }
}

function registerMediaSessionHandlers(store, audio) {
  if (!('mediaSession' in navigator)) {
    return;
  }
  const handlers = {
    play: () => store.resume(),
    pause: () => audio.pause(),
    stop: () => store.close(),
    seekbackward: (details) => store.skip(-(details.seekOffset || SKIP_BACK_SECONDS)),
    seekforward: (details) => store.skip(details.seekOffset || SKIP_FORWARD_SECONDS),
    seekto: (details) => store.seek(details.seekTime),
  };
  for (const [action, handler] of Object.entries(handlers)) {
    try {
      navigator.mediaSession.setActionHandler(action, handler);
    } catch {
      // Action not supported by this browser.
    }
  }
}

/**
 * Keep the loaded track across a full page load (e.g. a reload or a link that leaves the SPA
 * navigation), so the visitor can resume where they left off. Playback itself restarts only
 * on their click, as browsers block autoplay.
 */
function rememberSession(store, audio) {
  if (!store.track) {
    return;
  }
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ track: store.track, position: audio.currentTime, rate: store.rate }));
  } catch {
    // Storage unavailable (private mode, blocked cookies).
  }
}

function restoreSession(store) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null');
    if (saved?.track?.src) {
      store.rate = PLAYBACK_RATES.includes(saved.rate) ? saved.rate : 1;
      store.load(saved.track, { startAt: Number(saved.position) || 0 });
    }
  } catch {
    forgetSession();
  }
}

function forgetSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Storage unavailable.
  }
}
